define('presentation/signupPackagePicker/viewModels/signupPackagePickerViewModel',[
    'i18next',
    'businessServices/converters/currencyFormatter',
    'common/promises/promiseFactory',
    'constants/billingPackageTypeConstants',
    'presentation/signupPackagePicker/presentationObjects/signupPackagePresentationObject',
    'presentation/tabs/presentationObjects/tabsPresentationObject'
], function () {
    return function () {
        let self = this;

        const CurrencyFormatterConstructor = require('businessServices/converters/currencyFormatter');
        const _currencyFormatter = new CurrencyFormatterConstructor();

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const BillingPackagePresentationObjectConstructor = require('presentation/signupPackagePicker/presentationObjects/signupPackagePresentationObject');
        const TabPresentationObjectConstructor = require('presentation/tabs/presentationObjects/tabsPresentationObject');

        const _i18n = require('i18next');
        const _billingPackageTypes = require('constants/billingPackageTypeConstants');

        const TRANSLATION_KEYS = {
            INCLUDED: {
                MINUTES: 'signupPackagePicker:packageFeatures.includedMinutes',
                PHONE_NUMBERS: 'signupPackagePicker:packageFeatures.includedPhoneNumbers',
                USERS: 'signupPackagePicker:packageFeatures.includedUsers',
                CHATS: 'signupPackagePicker:packageFeatures.includedChats',
                CONTACTS: 'signupPackagePicker:packageFeatures.includedContacts',
                SCRIPTS: 'signupPackagePicker:packageFeatures.includedScripts'
            },
            ADDITIONAL: {
                MINUTES: 'signupPackagePicker:packageFeatures.additionalMinutes',
                PHONE_NUMBERS: 'signupPackagePicker:packageFeatures.additionalPhoneNumbers',
                USERS: 'signupPackagePicker:packageFeatures.additionalUsers',
                CHATS: 'signupPackagePicker:packageFeatures.additionalChats',
                CONTACTS: 'signupPackagePicker:packageFeatures.additionalContacts',
                SCRIPTS: 'signupPackagePicker:packageFeatures.additionalScripts'
            }
        };

        const readableValue = (value) => value === 999999 ? "Unlimited" : value;

        const _buildFeatureText = (translationKey, value) => {
            const count = readableValue(value);

            if (count === 'Unlimited') {
                return _i18n.t(`${translationKey}_unlimited`);
            }

            return _i18n.t(translationKey, {count: parseInt(count, 10)});
        };

        const _formatPriceDisplay = (i18nKey, price, isUSD) => {
            const formattedPrice = isUSD ? _currencyFormatter.formatUSDForDisplay(price) : _currencyFormatter.formatPlanPrice(price);
            return _i18n.t(`signupPackagePicker:packageFeatures.${i18nKey}`, {price: formattedPrice});
        };

        const _prepareBillingPackageForDisplay = (billingPackage) => {
            const billingPackagePresentationObject = new BillingPackagePresentationObjectConstructor();
            billingPackagePresentationObject.billingPackageId = billingPackage.billingPackageId;
            billingPackagePresentationObject.packageId = billingPackage.packageId;
            billingPackagePresentationObject.type = billingPackage.type;
            billingPackagePresentationObject.sequence = billingPackage.sequence;
            billingPackagePresentationObject.name = billingPackage.name;
            billingPackagePresentationObject.description = billingPackage.description;
            billingPackagePresentationObject.price = _currencyFormatter.formatPlanPrice(billingPackage.pricePerMonth);
            billingPackagePresentationObject.pricePerMonth = _formatPriceDisplay('pricePerMonth', billingPackage.pricePerMonth, true);

            billingPackagePresentationObject.isMostPopular = billingPackage.isMostPopular;
            billingPackagePresentationObject.isOnboardingRequired = billingPackage.isOnboardingRequired;
            billingPackagePresentationObject.isRetired = billingPackage.isRetired;
            billingPackagePresentationObject.isUpgradable = billingPackage.isUpgradable;
            billingPackagePresentationObject.isSignatureService = billingPackage.isSignatureService;

            billingPackagePresentationObject.features = [];

            if (billingPackage.includedChatSessions != null) {
                billingPackagePresentationObject.includedChatSessions = billingPackage.includedChatSessions;
                billingPackagePresentationObject.pricePerAdditionalChatSession = _formatPriceDisplay('pricePerChat', billingPackage.pricePerAdditionalChatSession);
                billingPackagePresentationObject.features.push(
                    {text: _buildFeatureText(TRANSLATION_KEYS.INCLUDED.CHATS, billingPackage.includedChatSessions)},
                    {text: _i18n.t(TRANSLATION_KEYS.ADDITIONAL.CHATS, {price: billingPackagePresentationObject.pricePerAdditionalChatSession})}
                );
            }

            if (billingPackage.includedOutboundContacts != null) {
                billingPackagePresentationObject.includedOutboundContacts = billingPackage.includedOutboundContacts;
                billingPackagePresentationObject.pricePerAdditionalOutboundContact = _formatPriceDisplay('pricePerContact', billingPackage.pricePerAdditionalOutboundContact);
                billingPackagePresentationObject.features.push(
                    {text: _buildFeatureText(TRANSLATION_KEYS.INCLUDED.CONTACTS, billingPackage.includedOutboundContacts)},
                    {text: _i18n.t(TRANSLATION_KEYS.ADDITIONAL.CONTACTS, {price: billingPackagePresentationObject.pricePerAdditionalOutboundContact})}
                );
            }

            if (billingPackage.includedMinutes != null) {
                billingPackagePresentationObject.includedMinutes = billingPackage.includedMinutes;
                billingPackagePresentationObject.pricePerAdditionalMinute = _formatPriceDisplay('pricePerMinute', billingPackage.pricePerAdditionalMinute, true);
                billingPackagePresentationObject.features.push(
                    {text: _buildFeatureText(TRANSLATION_KEYS.INCLUDED.MINUTES, billingPackage.includedMinutes)}
                );
                if (billingPackage.type !== _billingPackageTypes.telephony.id) {
                    billingPackagePresentationObject.features.push(
                        {text: _i18n.t(TRANSLATION_KEYS.ADDITIONAL.MINUTES, {price: billingPackagePresentationObject.pricePerAdditionalMinute})}
                    );
                }
            }

            billingPackagePresentationObject.includedPhoneNumbers = billingPackage.includedPhoneNumbers;
            billingPackagePresentationObject.pricePerAdditionalPhoneNumber = _formatPriceDisplay('pricePerNumber', billingPackage.pricePerAdditionalPhoneNumber);
            if (billingPackage.type === _billingPackageTypes.telephony.id) {
                billingPackagePresentationObject.features.push(
                    {text: _buildFeatureText(TRANSLATION_KEYS.INCLUDED.PHONE_NUMBERS, billingPackage.includedPhoneNumbers)}
                );
            }

            billingPackagePresentationObject.includedScripts = billingPackage.includedScripts;
            billingPackagePresentationObject.pricePerAdditionalScript = _formatPriceDisplay('pricePerScript', billingPackage.pricePerAdditionalScript);

            billingPackagePresentationObject.includedUsers = billingPackage.includedUsers;
            billingPackagePresentationObject.pricePerAdditionalUser = _formatPriceDisplay('pricePerUser', billingPackage.pricePerAdditionalUser);
            if (billingPackage.type === _billingPackageTypes.telephony.id) {
                billingPackagePresentationObject.features.push(
                    {text: _buildFeatureText(TRANSLATION_KEYS.INCLUDED.USERS, billingPackage.includedUsers)}
                );
            }

            billingPackagePresentationObject.outboundCallingSetupFee = _currencyFormatter.formatUSDForDisplay(billingPackage.outboundCallingSetupFee);
            billingPackagePresentationObject.smsSetupFee = _currencyFormatter.formatUSDForDisplay(billingPackage.smsSetupFee);

            billingPackagePresentationObject.isSelected = ko.pureComputed(() => {
                return self.selectedPackageId() === billingPackage.packageId;
            });

            billingPackagePresentationObject.isSelectable = ko.pureComputed(() => {
                return billingPackage.type !== _billingPackageTypes.telephony.id;
            });

            return billingPackagePresentationObject;
        };

        self.onSelectCallback = null;
        self.onCustomCallback = null;
        self.packageType = ko.observable(null);
        self.availablePackages = ko.observableArray([]);
        self.selectedPackageId = ko.observable(null);

        self.packagePickerTitle = ko.pureComputed(() => {
            switch (self.packageType()) {
                case _billingPackageTypes.liveReceptionist.id:
                    return _i18n.t("signupPackagePicker:packagePickerTitle", {context: _billingPackageTypes.liveReceptionist.key});
                case _billingPackageTypes.webChat.id:
                    return _i18n.t("signupPackagePicker:packagePickerTitle", {context: _billingPackageTypes.webChat.key});
                case _billingPackageTypes.outboundCalling.id:
                    return _i18n.t("signupPackagePicker:packagePickerTitle", {context: _billingPackageTypes.outboundCalling.key});
                default:
                    return "";
            }
        });

        self.displayPackages = ko.pureComputed(() => {
            return self.availablePackages()
                .filter((pkg) => {
                    return self.tabActive() === self.tabMetadata.signature.id ? pkg.isSignatureService : !pkg.isSignatureService;
                })
                .sort((a, b) => a.sequence - b.sequence);
        });

        self.showOutboundCallingSetupFee = ko.pureComputed(() => {
            return self.packageType() === _billingPackageTypes.outboundCalling.id;
        });

        self.showTabs = ko.pureComputed(() => {
            const availableSignatureServicePackages = self.availablePackages().filter((pkg) => pkg.type === self.packageType() && pkg.isSignatureService);
            return availableSignatureServicePackages.length > 0;
        });

        self.tabActive = ko.observable();
        self.tabMetadata = {
            classic: {
                id: "classic",
                isValid: ko.observable(true),
                title: _i18n.t('signupPackagePicker:packageTab.classic.label'),
                description: _i18n.t('signupPackagePicker:packageTab.classic.description'),
            },
            signature: {
                id: "signature",
                isValid: ko.observable(true),
                title: _i18n.t('signupPackagePicker:packageTab.signature.label'),
                description: _i18n.t('signupPackagePicker:packageTab.signature.description'),
            },
        };
        self.tabs = [
            new TabPresentationObjectConstructor(self.tabMetadata.classic.id, self.tabActive, self.tabMetadata.classic.isValid, self.tabMetadata.classic.title),
            new TabPresentationObjectConstructor(self.tabMetadata.signature.id, self.tabActive, self.tabMetadata.signature.isValid, self.tabMetadata.signature.title)
        ];

        self.tabActiveDescription = ko.pureComputed(() => {
            return self.tabActive() === self.tabMetadata.classic.id ? self.tabMetadata.classic.description : self.tabMetadata.signature.description;
        });

        self.toggleSelectedPackage = (billingPackage) => {
            self.selectedPackageId(billingPackage.packageId);
            if (self.onSelectCallback) {
                self.onSelectCallback(billingPackage);
            }
        };

        self.onNeedLargerPlanSelected = () => {
            if (self.onCustomCallback) {
                self.onCustomCallback();
            }
        };

        self.activate = ({ packageType, packageId, availablePackages, onSelectCallback = null, onCustomCallback = null}) => {
            return _initialize(packageType, packageId, availablePackages, onSelectCallback, onCustomCallback);
        };

        const _initialize = (packageType, packageId, availablePackages, onSelectCallback, onCustomCallback) => {
            self.packageType(ko.unwrap(packageType));

            if (ko.isObservable(packageId)) {
                self.selectedPackageId = packageId;
            } else {
                self.selectedPackageId(packageId);
            }

            self.onSelectCallback = onSelectCallback;
            self.onCustomCallback = onCustomCallback;

            const displayBillingPackages = ko.unwrap(availablePackages).map((p) => _prepareBillingPackageForDisplay(p));
            self.availablePackages(displayBillingPackages);

            self.tabActive(self.tabs[0].id);

            return _promiseFactory.wait();
        };
    };
});
